import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Users, RefreshCw, AlertTriangle, ShieldCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import UserTenants from './UserTenants';

// Labels for the keys served by GET /api/users/features. A key with no entry
// here still renders, under its raw name, so adding a feature server-side never
// leaves it ungrantable from this page.
const FEATURE_LABELS = {
  datasync: 'DataSync',
  time_analysis: 'Time Analysis',
  service_inbox: 'Service Inbox',
  // An add-on: counts only with Service Inbox (2026-09-29).
  service_inbox_mail: 'Service Inbox: manage and send mail',
  audit: 'Audit Tool',
};

const featureLabel = (key) => FEATURE_LABELS[key] || key;

const FeaturePicker = ({ options, value, onChange, isAdmin }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700">Features</label>
    <p className="text-xs text-gray-500 mt-0.5">
      {isAdmin
        ? 'Admins have access to everything; per-feature toggles do not apply.'
        : 'A user with none of these can sign in but reach nothing.'}
    </p>
    <div className="mt-2 space-y-1">
      {options.map((key) => (
        <label key={key} className="flex items-center">
          <input
            type="checkbox"
            className="mr-2"
            disabled={isAdmin}
            checked={isAdmin || value.includes(key)}
            onChange={(e) =>
              onChange(
                e.target.checked
                  ? [...value, key]
                  : value.filter((f) => f !== key)
              )
            }
          />
          <span className={isAdmin ? 'text-gray-400' : ''}>{featureLabel(key)}</span>
        </label>
      ))}
    </div>
  </div>
);

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [featureOptions, setFeatureOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tenantOptions, setTenantOptions] = useState([]);
  const [manageTenantsUserId, setManageTenantsUserId] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  // One save at a time: a second click on a slow save sent an overlapping
  // request (2026-09-29), so the button is disabled until the first returns.
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [createForm, setCreateForm] = useState({
    email: '',
    username: '',
    password: '',
    is_active: true,
    is_superuser: false,
    record_technician_views: false,
    tenant_ids_text: '',
    features: []
  });
  const [editForm, setEditForm] = useState({
    email: '',
    username: '',
    password: '',
    is_active: true,
    is_superuser: false,
    record_technician_views: false,
    features: []
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users');
      setUsers(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch users', err);
      setError(err.response?.data?.detail || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await axios.get('/api/users/tenants');
      setTenantOptions(response.data || []);
    } catch (err) {
      console.error('Failed to fetch tenants', err);
    }
  };

  const fetchFeatures = async () => {
    try {
      const response = await axios.get('/api/users/features');
      setFeatureOptions(response.data || []);
    } catch (err) {
      console.error('Failed to fetch features', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchTenants();
    fetchFeatures();
  }, []);

  const openCreate = () => {
    setCreateForm({
      email: '',
      username: '',
      password: '',
      is_active: true,
      is_superuser: false,
      record_technician_views: false,
      tenant_ids_text: '',
      features: []
    });
    setIsCreateOpen(true);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const tenant_ids = Array.from(createForm.tenant_ids || []);
      const payload = {
        email: createForm.email,
        username: createForm.username,
        password: createForm.password,
        is_active: createForm.is_active,
        is_superuser: createForm.is_superuser,
        record_technician_views: createForm.record_technician_views,
        tenant_ids: tenant_ids.length > 0 ? tenant_ids : null,
        features: createForm.features
      };
      await axios.post('/api/users', payload);
      toast.success('User created');
      setIsCreateOpen(false);
      await fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to create user';
      toast.error(detail);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const openEdit = (user) => {
    setSelectedUser(user);
    setEditForm({
      email: user.email,
      username: user.username,
      password: '',
      is_active: user.is_active,
      is_superuser: user.is_superuser,
      record_technician_views: user.record_technician_views,
      // Admins resolve to the whole catalog, so show them nothing ticked --
      // otherwise demoting one would look like it granted every feature.
      features: user.is_superuser ? [] : (user.features || [])
    });
    setIsEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!selectedUser || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const payload = {
        email: editForm.email,
        username: editForm.username,
        is_active: editForm.is_active,
        is_superuser: editForm.is_superuser,
        record_technician_views: editForm.record_technician_views,
        features: editForm.features
      };
      if (editForm.password && editForm.password.length > 0) {
        payload.password = editForm.password;
      }
      await axios.put(`/api/users/${selectedUser.id}`, payload);
      toast.success('User updated');
      setIsEditOpen(false);
      setSelectedUser(null);
      await fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to update user';
      toast.error(detail);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const toggleActive = async (user) => {
    try {
      if (user.is_active) {
        await axios.patch(`/api/users/${user.id}/deactivate`);
        toast.success('User deactivated');
      } else {
        await axios.patch(`/api/users/${user.id}/activate`);
        toast.success('User activated');
      }
      await fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to update status';
      toast.error(detail);
    }
  };

  const sendResetEmail = async (user) => {
    try {
      await axios.post('/api/auth/password/reset/request', { email: user.email });
      toast.success('Password reset email sent');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to send reset email';
      toast.error(detail);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Users className="h-6 w-6 text-blue-600 mr-2" />
          <h1 className="text-2xl font-semibold">User Management</h1>
        </div>
        <div className="space-x-2">
          <button
            onClick={fetchUsers}
            className="inline-flex items-center px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </button>
          <button
            onClick={openCreate}
            className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            + New User
          </button>
        </div>
      </div>


      {loading && (
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2">Loading users...</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {!loading && !error && (
        <div className="bg-white shadow rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenants</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Features</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((u) => (
                  <tr key={u.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm w-10 align-top">
                      <div className="flex flex-col">
                        <span className="text-gray-900 text-base font-medium inline-flex items-center gap-1.5">
                          {u.username}
                          {u.is_superuser && (
                            <ShieldCheck className="h-4 w-4 text-blue-600" title="Admin" />
                          )}
                        </span>
                        <span className="text-gray-500 text-medium">{u.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-blue-600 align-middle">
                      <button
                        type="button"
                        onClick={() => setManageTenantsUserId(u.id)}
                        className="hover:underline text-left whitespace-normal break-words block"
                        title="Manage Tenants"
                      >
                        {Array.isArray(u.tenant_ids) && u.tenant_ids.length > 0
                          ? u.tenant_ids
                              .map((id) => tenantOptions.find((t) => t.tenant_id === id)?.location || id)
                              .join(', ')
                          : 'All tenants'}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm align-middle">
                      {u.is_superuser ? (
                        <span className="text-gray-500">All (admin)</span>
                      ) : Array.isArray(u.features) && u.features.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {u.features.map((f) => (
                            <span key={f} className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
                              {featureLabel(f)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">None</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm align-middle">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${u.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm align-middle">
                      <div className="flex gap-2 justify-end items-center">
                        <button
                          onClick={() => openEdit(u)}
                          className="inline-flex px-2.5 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap w-auto"
                        >
                          Edit User
                        </button>
                        <button
                          onClick={() => sendResetEmail(u)}
                          className="inline-flex px-2.5 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap w-auto"
                        >
                          Send Reset
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Manage Tenants Modal (Admin) */}
      {manageTenantsUserId && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl font-semibold">Manage Tenants</h2>
              <button onClick={() => { setManageTenantsUserId(null); fetchUsers(); }} className="px-3 py-1 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Close</button>
            </div>
            <div className="max-h-[80vh] overflow-auto">
              <UserTenants userId={manageTenantsUserId} />
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {isCreateOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Create User</h2>
            <form onSubmit={submitCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  required
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Username</label>
                <input
                  type="text"
                  required
                  value={createForm.username}
                  onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <input
                  type="password"
                  required
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-base py-2.5 px-3"
                  autoComplete="new-password"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={createForm.is_active}
                    onChange={(e) => setCreateForm({ ...createForm, is_active: e.target.checked })}
                    className="mr-2"
                  />
                  Active
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={createForm.is_superuser}
                    onChange={(e) => setCreateForm({ ...createForm, is_superuser: e.target.checked })}
                    className="mr-2"
                  />
                  Admin
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={createForm.record_technician_views}
                    onChange={(e) => setCreateForm({ ...createForm, record_technician_views: e.target.checked })}
                    className="mr-2"
                  />
                  Record technician views
                </label>
              </div>
              <FeaturePicker
                options={featureOptions}
                value={createForm.features}
                onChange={(features) => setCreateForm({ ...createForm, features })}
                isAdmin={createForm.is_superuser}
              />
              {/* Tenant selection removed: use Manage Tenants after creation to assign */}
              <div className="flex justify-end space-x-2">
                <button type="button" onClick={() => setIsCreateOpen(false)} className="px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{saving ? 'Creating…' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {isEditOpen && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Edit User</h2>
            <form onSubmit={submitEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  required
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Username</label>
                <input
                  type="text"
                  required
                  value={editForm.username}
                  onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">New Password (optional)</label>
                <input
                  type="password"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-base py-2.5 px-3"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={editForm.is_active}
                    onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                    className="mr-2"
                  />
                  Active
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={editForm.is_superuser}
                    onChange={(e) => setEditForm({ ...editForm, is_superuser: e.target.checked })}
                    className="mr-2"
                  />
                  Admin
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={editForm.record_technician_views}
                    onChange={(e) => setEditForm({ ...editForm, record_technician_views: e.target.checked })}
                    className="mr-2"
                  />
                  Record technician views
                </label>
              </div>
              <FeaturePicker
                options={featureOptions}
                value={editForm.features}
                onChange={(features) => setEditForm({ ...editForm, features })}
                isAdmin={editForm.is_superuser}
              />
              <div className="flex justify-end space-x-2">
                <button type="button" onClick={() => { setIsEditOpen(false); setSelectedUser(null); }} className="px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;


